import { Action } from '../types/types';
import * as A from '../actions/content-chips';
import { handle } from 'redux-pack';
import { GeneratedContent } from '../types/content';
import { HangingUserKey } from './generate-content';
import { CONTENT_ENTITY_UPDATED } from '../actions/generate-content';
import { Chipset, FieldedChip } from '../../components/types/stylepacks';
import { SetChipset } from '../../connection/content-chips';

const createChipMap = (chips: FieldedChip[]) => {
	const nKeyedChips = chips.reduce((acc: ChipMap, curr: FieldedChip) => {
		acc.set(curr.data_key, curr);
		return acc;
	}, new Map());
	return nKeyedChips;
};

type ChipMap = Map<string, FieldedChip>
export interface ContentChipsState {
	hangingUserIds: Map<string, number>;
	chipsetUser: number | null;
	chipMap: ChipMap;
	channel: number | null;
	loadedChip: number | null;
	loadingChip: boolean;
	draftChipset: Chipset | null;
}

export const initialContentChipsState: ContentChipsState = {
	hangingUserIds: new Map(),
	channel: null,
	chipsetUser: null,
	chipMap: new Map(),
	loadedChip: null,
	loadingChip: true,
	draftChipset: null,
}

const actions: Map<string, (
	statestore: ContentChipsState,
	action: Action
) => ContentChipsState> = new Map([
	[A.SET_CHANNEL, (
		state: ContentChipsState,
		action: Action
	) => ({...state, channel: action.payload})],
	[A.SET_HANGING_USER_ID, (
		state: ContentChipsState,
		action: Action
	) => {
		const {userId, uniqueContentId} = action.payload;
		if (userId) {
			state.hangingUserIds.set(uniqueContentId, userId);
		} else {
			state.hangingUserIds.delete(uniqueContentId);
		}
		return state;
	}],
	[A.LOAD_CHIPSET, (s: ContentChipsState, a: Action) => handle(s, a, {
		start: (state) => ({
			...state, 
			loadingChip: true,
		}),
		finish: (state) => state,
		failure: (state) => ({
			...state,
			loadingChip: false
		}),
		success: (state) => {
			return {
				...state,
				chipsetUser: a?.payload?.gr_user ?? 0,
				chipMap: a?.payload?.chips ? createChipMap(a.payload.chips) : new Map(),
				loadedChip: a?.payload?.id ?? 0,
				loadingChip: false,
				draftChipset: null,
			}
		},
	})],
	[A.LOAD_CHIPSET_DRAFT, (s: ContentChipsState, a: Action) => handle(s, a, {
		start: (state) => ({
			...state,
			loadingChip: true,
		}),
		finish: (state) => state,
		failure: (state) => ({
			...state,
			loadingChip: false
		}),
		success: (state) => {
			const chipset = a?.payload?.entityValue ?? null;
			return {
				...state,
				chipsetUser: chipset?.gr_user ?? 0,
				chipMap: chipset?.chips ? createChipMap(chipset.chips) : new Map(),
				loadedChip: chipset?.id ?? 0,
				loadingChip: false,
				draftChipset: chipset,
			}
		},
	})],
	[A.CLEAR_CHIPSET, (s: ContentChipsState) => {
		return {
			...s,
			chipsetUser: null,
			chipMap: new Map(),
			loadedChip: null,
			loadingChip: true
		};
	}],
	[A.CLEAR_CHIPSET_DRAFT, (s: ContentChipsState) => {
		return {
			...s,
			draftChipset: null,
		};
	}],
	[A.UPDATE_CHIPSET_DRAFT, (s: ContentChipsState, a: Action) => {
		if (!s.draftChipset || a?.payload?.entityValue?.id !== s.loadedChip) {
			return s;
		}

		return {
			...s,
			chipsetUser: a?.payload?.entityValue?.gr_user ?? 0,
			chipMap: a?.payload?.entityValue?.chips ? createChipMap(a.payload.entityValue.chips) : new Map(),
			loadedChip: a?.payload?.entityValue?.id ?? 0,
			loadingChip: false,
			draftChipset: a?.payload?.entityValue ?? null,
		};
	}],
	[CONTENT_ENTITY_UPDATED, (s: ContentChipsState, a?: {payload?: {entityValue?: GeneratedContent}}) => {
		// a content entity was updated, check for if there was a user chipset we should attach
		if (!a?.payload?.entityValue) {
			console.warn('entity updated without entity value');
			return s;
		}
		const {id, fkTable, fkId} = a?.payload?.entityValue;
		const safeInt = parseInt(`${id ?? ''}`, 10);
		const safeFkInt = parseInt(`${fkId ?? ''}`, 10);
		if (Number.isNaN(safeInt + safeFkInt)) {
			console.warn('unsafe id on entity update for generated content', safeInt, safeFkInt);
			return s;
		}
		const user = s.hangingUserIds.get(HangingUserKey(fkTable, safeFkInt));
		if (user && s.channel) {
			SetChipset(s.channel, safeInt, user);
			s.hangingUserIds.delete(HangingUserKey(fkTable, safeFkInt))
		}
		return s;
	}],
]);

export function ContentChipsReducer(s: ContentChipsState = initialContentChipsState, action: Action) {
	return actions.get(action.type)?.(s, action) || s;
}